import React from 'react'

import { Box, Button, Center, Flex, Stack, Text } from '@chakra-ui/react'
import { MemberLabel } from 'src/components/MemberLabel/MemberLabel'
import { HEADER_HEIGHT } from 'src/features/misc/components'
import { FeatureChannel, TrialMemberRole } from 'src/lib/gql-client'

import { ExplanationRoomHeader } from '../ExplanationRoomHeader/ExplanationRoomHeader'
import { PatientLeaveRoomButton } from '../PatientLeaveRoomButton/PatientLeaveRoomButton'

type Props = {
  children: React.ReactNode
  trialUid: string
  featureChannel: FeatureChannel
  sidebarInfo: SidebarInfo
  canMoveToRoom: boolean
  isAgreementCompletedOrRejected: boolean
  onMoveToRoom: () => void
}

export const PatientLobbyLayout: React.FC<Props> = ({
  children,
  trialUid,
  featureChannel,
  sidebarInfo,
  canMoveToRoom,
  isAgreementCompletedOrRejected,
  onMoveToRoom,
}) => {
  return (
    <Box h="full">
      <Flex width="100%" height="100%" direction="column">
        <ExplanationRoomHeader
          featureChannel={featureChannel}
          headerTitle="説明ロビー"
          height={HEADER_HEIGHT}
          finishButton={
            <PatientLeaveRoomButton
              trialUid={trialUid}
              isAgreementCompletedOrRejected={isAgreementCompletedOrRejected}
            />
          }
        />
        <Flex height={`calc(100% - ${HEADER_HEIGHT}px)`}>
          <Box as="main" flex="1" overflowY="auto" h="full" bg="green.50">
            {children}
          </Box>
          <Box
            w={{ base: '240px', lg: '360px' }}
            p="6"
            bg="white"
            height="full"
            overflow="auto"
          >
            <Sidebar
              sidebarInfo={sidebarInfo}
              canMoveToRoom={canMoveToRoom}
              onMoveToRoom={onMoveToRoom}
            />
          </Box>
        </Flex>
      </Flex>
    </Box>
  )
}

type SidebarMember = {
  index: number
  role: TrialMemberRole
  name: string
}

type SidebarInfo = {
  trialName: string
  hospitalName: string
  candidateId: string
  members: SidebarMember[]
}

type SidebarProps = {
  sidebarInfo: SidebarInfo
  canMoveToRoom: boolean
  onMoveToRoom: () => void
}

const Sidebar: React.FC<SidebarProps> = ({
  sidebarInfo,
  onMoveToRoom,
  canMoveToRoom,
}) => {
  const { trialName, candidateId, hospitalName, members } = sidebarInfo
  return (
    <Flex w="full" h="full" direction="column" justify="space-between">
      <Stack spacing="8">
        <Text fontSize="xl" fontWeight="bold">
          {trialName}
        </Text>
        {candidateId && (
          <Stack spacing="2">
            <Text fontSize="xl" fontWeight="bold">
              候補ID
            </Text>
            <Text>{candidateId}</Text>
          </Stack>
        )}
        <Box>
          <Text fontSize="xl" fontWeight="bold">
            担当者
          </Text>
          <Box mt="2">
            <Text fontWeight="bold" as="h3">
              {hospitalName}
            </Text>
            <Stack mt="2" spacing="1" ml="6">
              {members
                .sort((a, b) => a.index - b.index)
                .map(m => (
                  <MemberLabel
                    key={m.index}
                    role={m.role}
                    displayName={m.name}
                  />
                ))}
            </Stack>
          </Box>
        </Box>
      </Stack>

      <Center>
        <Button
          colorScheme="green"
          onClick={onMoveToRoom}
          isDisabled={!canMoveToRoom}
        >
          説明ルームへ
        </Button>
      </Center>
    </Flex>
  )
}
